import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PageContainer from "./layout/PageContainer.js";
import JsonLd from "./JsonLd.js";
import {
  COMMON_QUESTIONS,
  COMMON_QUESTIONS_PAGE,
  buildCommonQuestionsIndexJsonLd,
  commonQuestionPath,
} from "../constants/commonQuestions.js";
import "../mental-clarity-guide.css";

const DEFAULT_TITLE = "Ahavibes — Stop reacting. Start thinking clearly.";
const DEFAULT_DESCRIPTION =
  "A guided framework to examine interpretations, reduce mental noise, and make better decisions.";

const TEASER_MAX = 140;

function matchesQuery(item, query) {
  if (!query) return true;
  const relatedLabels = (item.relatedGuides || [])
    .map((g) => g.label)
    .concat(item.guideLabel || []);
  const haystack = [
    item.question,
    ...item.answerParagraphs,
    ...(item.askYourself || []),
    item.tryAhavibes,
    item.metaDescription,
    ...relatedLabels,
    ...(item.keywords || []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(query);
}

function teaserFor(item) {
  const raw = (item.metaDescription || item.answerParagraphs?.[0] || "").trim();
  if (raw.length <= TEASER_MAX) return raw;
  const cut = raw.slice(0, TEASER_MAX);
  const lastSpace = cut.lastIndexOf(" ");
  return `${(lastSpace > 80 ? cut.slice(0, lastSpace) : cut).trim()}…`;
}

const CommonQuestions = () => {
  const [query, setQuery] = useState("");

  const normalizedQuery = query.trim().toLowerCase();
  const filtered = useMemo(
    () => COMMON_QUESTIONS.filter((item) => matchesQuery(item, normalizedQuery)),
    [normalizedQuery]
  );

  const jsonLd = useMemo(
    () => buildCommonQuestionsIndexJsonLd(COMMON_QUESTIONS_PAGE, COMMON_QUESTIONS),
    []
  );

  useEffect(() => {
    document.title = COMMON_QUESTIONS_PAGE.pageTitle;

    let meta = document.querySelector('meta[name="description"]');
    const createdMeta = !meta;
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    const previousDescription = meta.getAttribute("content");
    meta.setAttribute("content", COMMON_QUESTIONS_PAGE.description);

    return () => {
      document.title = DEFAULT_TITLE;
      if (createdMeta) {
        meta.remove();
      } else {
        meta.setAttribute("content", previousDescription || DEFAULT_DESCRIPTION);
      }
    };
  }, []);

  return (
    <>
      <JsonLd data={jsonLd} id="common-questions-jsonld" />
      <PageContainer size="wide" className="mental-clarity-guide">
        <div className="common-questions">
          <header className="common-questions__hero">
            <figure className="common-questions__hero-figure">
              <img
                src={COMMON_QUESTIONS_PAGE.heroIllustration}
                alt={COMMON_QUESTIONS_PAGE.heroIllustrationAlt}
                className="common-questions__hero-img"
                width={480}
                height={360}
                loading="eager"
                decoding="async"
              />
            </figure>
            <div className="common-questions__hero-text">
              <p className="common-questions__eyebrow">From the Ahavibes Guide</p>
              <h1 className="common-questions__title">{COMMON_QUESTIONS_PAGE.title}</h1>
              <p className="common-questions__subhead">{COMMON_QUESTIONS_PAGE.subhead}</p>
              <p className="common-questions__lead">{COMMON_QUESTIONS_PAGE.lead}</p>
            </div>
          </header>

          <div className="common-questions__toolbar">
            <label className="common-questions__search-label" htmlFor="common-questions-search">
              Search questions
            </label>
            <input
              id="common-questions-search"
              type="search"
              className="common-questions__search"
              placeholder="Search overthinking, clarity, decisions…"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              autoComplete="off"
            />
          </div>

          {filtered.length === 0 ? (
            <p className="common-questions__empty">
              No questions match that search. Try a broader word like &quot;loop,&quot;
              &quot;clarity,&quot; or &quot;decision.&quot;
            </p>
          ) : (
            <ul className="common-questions__grid">
              {filtered.map((item, index) => (
                <li key={item.id} className="common-questions__grid-item">
                  <Link to={commonQuestionPath(item.id)} className="common-questions__card">
                    <figure className="common-questions__card-figure">
                      <img
                        src={item.illustration}
                        alt={item.illustrationAlt || ""}
                        className="common-questions__card-img"
                        width={360}
                        height={270}
                        loading={index < 3 ? "eager" : "lazy"}
                        decoding="async"
                      />
                    </figure>
                    <h2 className="common-questions__card-title">{item.question}</h2>
                    <p className="common-questions__card-teaser">{teaserFor(item)}</p>
                  </Link>
                </li>
              ))}
            </ul>
          )}

          <nav className="guide-section-nav" aria-label="Guide navigation">
            <div className="guide-section-nav__links">
              <Link to="/guide" className="guide-section-nav__link guide-section-nav__link--prev">
                &larr; All Guides
              </Link>
              <Link
                to="/?tab=brain-dump"
                className="guide-section-nav__link guide-section-nav__link--next"
              >
                Start a reflection →
              </Link>
            </div>
          </nav>
        </div>
      </PageContainer>
    </>
  );
};

export default CommonQuestions;
